/**
 * Battleground store — match metadata for PvP HUD: status, scores,
 * flag positions (CTF), control points (resource-control), end summary.
 * Players + combat events flow through the shared net-store.
 *
 * R232q5aI: extended for WoTLK 3.3.5a multi-mode BG support — added
 * bgId / mode / control points / resource scores + setters.
 */

import { create } from 'zustand';

export interface BgFlagView {
  team: 'blue' | 'red';
  status: 'home' | 'carried' | 'dropped';
  pos: { x: number; z: number };
  homePos: { x: number; z: number };
  carrier: string;
}

export interface BgControlPointView {
  id: string;
  name: string;
  x: number;
  z: number;
  radius: number;
  owner: 'neutral' | 'blue' | 'red';
  status: string;       // '' | 'contested-blue' | 'contested-red'
  progress: number;     // 0..1000
  channeller: string;
  ownerLockUntil: number;
}

/** R232q5aL: Alterac Valley HUD — generals/captains hp + reinforcement bars.
 *  Populated from `bg.av-init` + `bg.npc-damage` + `bg.npc-killed`. */
export interface BgAvNpc {
  id: string;
  kind: 'general' | 'captain';
  team: 'blue' | 'red';
  name: string;
  x: number;
  z: number;
  hp: number;
  hpMax: number;
}
export interface BgAvHud {
  reinfBlue: number;
  reinfRed: number;
  npcs: Record<string, BgAvNpc>;
}

/** R232q5aL: Strand of the Ancients — round indicator, gates HP, demolisher count. */
export interface BgSotaGate {
  id: string;
  hp: number;
  hpMax: number;
}
export interface BgSotaVehicle {
  id: string;
  x: number;
  z: number;
  hp: number;
  hpMax: number;
  driver: string;
}
export interface BgSotaHud {
  round: 1 | 2;
  attacker: 'blue' | 'red';
  /** Round-elapsed ms from last sota-hud broadcast. */
  elapsedMs: number;
  /** Round duration cap from sota-init (ms). */
  roundMs: number;
  intermissionMs: number;
  gates: BgSotaGate[];
  vehicles: BgSotaVehicle[];
  /** Best-of-2 round results — present once a round ends. */
  results: Array<{ round: 1 | 2; attacker: 'blue' | 'red'; reachedRelic: boolean; elapsedMs: number }>;
}

/** R232q5aL: Isle of Conquest — generals + perk indicators per team. */
export interface BgIocGeneral {
  id: string;
  team: 'blue' | 'red';
  name: string;
  x: number;
  z: number;
  hp: number;
  hpMax: number;
}
export interface BgIocPerk {
  team: 'blue' | 'red';
  quarryReductionPct: number;
  refineryAmpPct: number;
}
export interface BgIocHud {
  generals: Record<string, BgIocGeneral>;
  perksBlue: { quarryReductionPct: number; refineryAmpPct: number };
  perksRed:  { quarryReductionPct: number; refineryAmpPct: number };
}

interface BgStore {
  active: boolean;
  bgId: string;
  mode: string;
  status: 'waiting' | 'starting' | 'live' | 'ended';
  /** CTF caps. */
  scoreBlue: number;
  scoreRed: number;
  scoreCap: number;
  /** Resource-control. */
  resBlue: number;
  resRed: number;
  resCap: number;
  startedAt: number;
  endsAt: number;
  winner: string;
  flags: BgFlagView[];
  controlPoints: BgControlPointView[];
  /** R232q5aL HUD slices for AV / SotA / IoC. Each is null until the
   *  corresponding `bg.*-init` broadcast lands. */
  av: BgAvHud | null;
  sota: BgSotaHud | null;
  ioc: BgIocHud | null;
  /** Team assignment of the local player. */
  myTeam: 'blue' | 'red' | null;
  setActive: (a: boolean) => void;
  setBgId: (id: string) => void;
  setMode: (m: string) => void;
  setStatus: (s: 'waiting' | 'starting' | 'live' | 'ended') => void;
  setScores: (blue: number, red: number) => void;
  setScoreCap: (n: number) => void;
  setResources: (blue: number, red: number) => void;
  setResCap: (n: number) => void;
  setStartedAt: (n: number) => void;
  setEndsAt: (n: number) => void;
  setWinner: (w: string) => void;
  setFlags: (f: BgFlagView[]) => void;
  setControlPoints: (cp: BgControlPointView[]) => void;
  setAv: (h: BgAvHud | null) => void;
  setSota: (h: BgSotaHud | null) => void;
  setIoc: (h: BgIocHud | null) => void;
  setMyTeam: (t: 'blue' | 'red' | null) => void;
  reset: () => void;
}

const initial = {
  active: false,
  bgId: 'warsong-gulch',
  mode: 'ctf',
  status: 'waiting' as const,
  scoreBlue: 0,
  scoreRed: 0,
  scoreCap: 3,
  resBlue: 0,
  resRed: 0,
  resCap: 1600,
  startedAt: 0,
  endsAt: 0,
  winner: '',
  flags: [] as BgFlagView[],
  controlPoints: [] as BgControlPointView[],
  av: null as BgAvHud | null,
  sota: null as BgSotaHud | null,
  ioc: null as BgIocHud | null,
  myTeam: null as 'blue' | 'red' | null,
};

export const useBg = create<BgStore>((set) => ({
  ...initial,
  setActive: (a) => set({ active: a }),
  setBgId: (id) => set({ bgId: id }),
  setMode: (m) => set({ mode: m }),
  setStatus: (s) => set({ status: s }),
  setScores: (blue, red) => set({ scoreBlue: blue, scoreRed: red }),
  setScoreCap: (n) => set({ scoreCap: n }),
  setResources: (blue, red) => set({ resBlue: blue, resRed: red }),
  setResCap: (n) => set({ resCap: n }),
  setStartedAt: (n) => set({ startedAt: n }),
  setEndsAt: (n) => set({ endsAt: n }),
  setWinner: (w) => set({ winner: w }),
  setFlags: (f) => set({ flags: f }),
  setControlPoints: (cp) => set({ controlPoints: cp }),
  setAv: (h) => set({ av: h }),
  setSota: (h) => set({ sota: h }),
  setIoc: (h) => set({ ioc: h }),
  setMyTeam: (t) => set({ myTeam: t }),
  reset: () => set(initial),
}));
